import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import axios from 'axios';
import { Button } from '../ui/button';
import {
  Wand2, ArrowRight, Loader2, CheckCircle2, AlertTriangle,
  TrendingDown, TrendingUp, Lock, Sparkles,
} from 'lucide-react';
import { toast } from 'sonner';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const VARS = ['purchase_price', 'rent', 'down_payment', 'interest_rate'];

const labelKey = (v) => ({
  purchase_price: 'scenario.purchasePrice',
  rent: 'scenario.annualRent',
  down_payment: 'scenario.downPayment',
  interest_rate: 'scenario.interestRate',
}[v]);

const fmtValue = (v, val) => {
  if (val === null || val === undefined) return '—';
  if (v === 'purchase_price') return `$${Math.round(val).toLocaleString()}`;
  if (v === 'rent') return `$${Math.round(val / 12).toLocaleString()}/mo`;
  if (v === 'down_payment') return `${val.toFixed(1)}%`;
  if (v === 'interest_rate') return `${val.toFixed(2)}%`;
  return String(val);
};

const scenarioFieldOf = (v) => ({
  purchase_price: 'purchase_price',
  rent: 'annual_income',
  down_payment: 'down_payment_percent',
  interest_rate: 'interest_rate',
}[v]);

const AdjustmentRow = ({ v, adj, locked }) => {
  const { t } = useTranslation();
  const changed = adj.changed;
  const dirIcon = v === 'purchase_price' || v === 'interest_rate' ? TrendingDown : TrendingUp;
  const DirIcon = dirIcon;
  return (
    <div data-testid={`bc-row-${v}`} className="grid grid-cols-[minmax(0,1fr)_auto_auto_auto] gap-2 items-center py-2 border-b border-slate-100 dark:border-slate-800 last:border-b-0">
      <div className="flex items-center gap-2 text-sm text-slate-700 dark:text-slate-300">
        {locked && <Lock className="w-3 h-3 text-amber-500" data-testid={`bc-locked-${v}`} />}
        <span>{t(labelKey(v))}</span>
      </div>
      <span data-testid={`bc-from-${v}`} className="font-mono tabular-nums text-slate-500 dark:text-slate-400 text-sm">
        {fmtValue(v, adj.from)}
      </span>
      <ArrowRight className={`w-3.5 h-3.5 ${changed ? 'text-violet-500' : 'text-slate-300 dark:text-slate-700'}`} />
      <span data-testid={`bc-to-${v}`} className={`font-mono tabular-nums text-sm font-semibold ${
        changed ? 'text-violet-700 dark:text-violet-300' : 'text-slate-500 dark:text-slate-400'
      }`}>
        {fmtValue(v, adj.to)}
        {changed && (
          <span className="ml-1 text-[10px] font-normal opacity-70 inline-flex items-center gap-0.5">
            <DirIcon className="w-3 h-3" />
            {adj.magnitude_unit === 'pct'
              ? `${adj.magnitude.toFixed(1)}%`
              : `${adj.magnitude.toFixed(2)}pp`}
          </span>
        )}
      </span>
    </div>
  );
};

const BestCombination = ({ scenario, locks, targetCashFlow, onApply }) => {
  const { t } = useTranslation();
  const [loading, setLoading] = useState(false);
  const [result, setResult] = useState(null);

  const run = async () => {
    setLoading(true);
    setResult(null);
    try {
      const lockedList = Object.entries(locks || {}).filter(([, v]) => v).map(([k]) => k);
      const resp = await axios.post(
        `${BACKEND_URL}/api/scenario/best-combination`,
        { scenario, target_monthly_cash_flow: targetCashFlow, locked: lockedList },
        { withCredentials: true }
      );
      setResult(resp.data);
    } catch (e) {
      const detail = e.response?.data?.detail;
      toast.error(detail || t('bestCombo.errorGeneric'));
    } finally {
      setLoading(false);
    }
  };

  const apply = () => {
    if (!result || result.status !== 'solved') return;
    // Only patch the four adjustable variables; do NOT overwrite any
    // Original assumption fields the user hasn't touched.
    const rec = result.recommended_scenario;
    const patch = {
      purchase_price: rec.purchase_price,
      annual_income: rec.annual_income,
      down_payment_percent: rec.down_payment_percent,
      interest_rate: rec.interest_rate,
    };
    onApply(patch);
    toast.success(t('bestCombo.applied'));
    setResult(null);
  };

  const keep = () => {
    setResult(null);
    toast.info(t('bestCombo.kept'));
  };

  const status = result?.status;
  const isSolved = status === 'solved';
  const isAlreadyMet = status === 'already_met';
  const isImpossible = status === 'impossible';

  return (
    <section
      data-testid="best-combination-section"
      role="region"
      aria-live="polite"
      aria-atomic="false"
      className="mt-8 rounded-xl p-6 shadow-sm border border-violet-200 dark:border-violet-800 bg-gradient-to-br from-violet-50 to-white dark:from-[#1a1830] dark:to-[#131824]"
    >
      <div className="flex items-start justify-between gap-3 mb-4 flex-wrap">
        <div>
          <div className="flex items-center gap-2">
            <Wand2 className="w-5 h-5 text-violet-500" />
            <h2 className="font-primary font-bold text-xl text-slate-900 dark:text-slate-100">
              {t('bestCombo.title')}
            </h2>
          </div>
          <p className="text-xs text-slate-500 dark:text-slate-400 mt-1 max-w-xl">
            {t('bestCombo.subtitle')}
          </p>
        </div>
        <Button
          data-testid="best-combination-run-btn"
          onClick={run}
          disabled={loading}
          className="bg-violet-600 hover:bg-violet-700 text-white"
        >
          {loading ? (<><Loader2 className="w-4 h-4 mr-2 animate-spin" />{t('bestCombo.computing')}</>)
                   : (<><Sparkles className="w-4 h-4 mr-2" />{t('bestCombo.makeItWork')}</>)}
        </Button>
      </div>

      {isAlreadyMet && (
        <div data-testid="bc-status-already-met" className="rounded-lg border border-emerald-200 dark:border-emerald-800 bg-emerald-50 dark:bg-emerald-950/20 p-4 flex items-start gap-3">
          <CheckCircle2 className="w-5 h-5 text-emerald-600 dark:text-emerald-400 flex-shrink-0 mt-0.5" />
          <div>
            <p className="font-semibold text-emerald-800 dark:text-emerald-300">{t('bestCombo.alreadyMet')}</p>
            <p className="text-xs text-emerald-700 dark:text-emerald-400 mt-1">{t('bestCombo.alreadyMetHint', { target: `$${targetCashFlow}` })}</p>
          </div>
        </div>
      )}

      {isImpossible && (
        <div data-testid="bc-status-impossible" className="rounded-lg border border-amber-300 dark:border-amber-800 bg-amber-50 dark:bg-amber-950/20 p-4">
          <div className="flex items-start gap-3">
            <AlertTriangle className="w-5 h-5 text-amber-600 dark:text-amber-400 flex-shrink-0 mt-0.5" />
            <div className="flex-1">
              <p className="font-semibold text-amber-800 dark:text-amber-300">
                {t(result.reason_code === 'all_variables_locked' ? 'bestCombo.impossibleAllLocked' : 'bestCombo.impossibleBounds')}
              </p>
              {result.binding_variables?.length > 0 && (
                <p data-testid="bc-binding" className="text-xs text-amber-700 dark:text-amber-400 mt-2">
                  {t('bestCombo.bindingVariables')}: {result.binding_variables.map((v) => t(labelKey(v))).join(', ')}
                </p>
              )}
            </div>
          </div>
        </div>
      )}

      {isSolved && (
        <div data-testid="bc-status-solved" className="space-y-4">
          {/* Adjustments list */}
          <div className="rounded-lg border border-violet-200 dark:border-violet-800 bg-white dark:bg-[#0F1419] p-4">
            <p className="text-xs uppercase tracking-wider text-violet-600 dark:text-violet-400 font-semibold mb-2">
              {t('bestCombo.recommendedAdjustments')}
            </p>
            {VARS.map((v) => (
              <AdjustmentRow
                key={v}
                v={v}
                adj={result.adjustments[v]}
                locked={result.locked_variables.includes(v)}
              />
            ))}
          </div>

          {/* Summary + Actions */}
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
            <div className="rounded-lg border border-slate-200 dark:border-slate-800 p-3">
              <p className="text-[10px] uppercase tracking-wider text-slate-500 dark:text-slate-400">{t('bestCombo.projectedMcf')}</p>
              <p data-testid="bc-achieved-mcf" className="font-primary font-bold text-lg text-emerald-600 dark:text-emerald-400 tabular-nums">
                {result.achieved_mcf >= 0 ? '+' : ''}${Math.round(result.achieved_mcf).toLocaleString()}/mo
              </p>
            </div>
            <div className="rounded-lg border border-slate-200 dark:border-slate-800 p-3">
              <p className="text-[10px] uppercase tracking-wider text-slate-500 dark:text-slate-400">{t('bestCombo.changedVariables')}</p>
              <p data-testid="bc-changed-count" className="font-primary font-bold text-lg text-slate-900 dark:text-slate-100">
                {result.changed_variables.length} / 4
              </p>
            </div>
            <div className="rounded-lg border border-slate-200 dark:border-slate-800 p-3">
              <p className="text-[10px] uppercase tracking-wider text-slate-500 dark:text-slate-400">{t('bestCombo.normalizedCost')}</p>
              <p data-testid="bc-cost" className="font-primary font-bold text-lg text-slate-900 dark:text-slate-100 tabular-nums">
                {result.normalized_cost?.total?.toFixed(3) ?? '—'}
              </p>
            </div>
          </div>

          <p className="text-[11px] text-slate-500 dark:text-slate-400">
            {t('bestCombo.deterministicNote')}
          </p>

          <div className="flex flex-wrap gap-2 pt-2 border-t border-violet-100 dark:border-violet-900">
            <Button
              data-testid="bc-apply-btn"
              onClick={apply}
              className="bg-violet-600 hover:bg-violet-700 text-white"
            >
              {t('bestCombo.applyScenario')}
            </Button>
            <Button
              data-testid="bc-keep-btn"
              onClick={keep}
              variant="outline"
              className="border-slate-300 dark:border-slate-700"
            >
              {t('bestCombo.keepCurrent')}
            </Button>
          </div>
        </div>
      )}
    </section>
  );
};

export default BestCombination;
